import { AccessLevel } from "@/api/williams/users";
import { ConfirmationDialog } from "@/components/dialogs";
import { faBan, faCheck } from "@fortawesome/free-solid-svg-icons";
import { faFilePdf, faSave, faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { IconButton } from "../../components";
import { ScopeOfWork, ScopeOfWorkStatus } from "@/api/williams/scopesOfWork";
import { useCancelScope, useCloseScope, useDeleteScope, usePrintScope, useSaveScope } from "../hooks";
import { useFormContext } from "react-hook-form";
import { useLoadingScreen, useModalDialog } from "@/hooks";
import { usePermissions } from "../../users/hooks";

export type ToolbarProps = {
  data: ScopeOfWork;
};

export function SOWFormToolbar({ data }: Readonly<ToolbarProps>) {
  const save = useSaveScope();
  const close = useCloseScope();
  const cancel = useCancelScope();
  const deleteScope = useDeleteScope(true);
  const print = usePrintScope();
  const dialog = useModalDialog();
  const permissions = usePermissions();
  const loader = useLoadingScreen();

  const {
    reset,
    handleSubmit,
    getValues,
    formState: { dirtyFields },
  } = useFormContext<ScopeOfWork>();

  const role = permissions.data?.accessLevel;
  const isDirty = Object.keys(dirtyFields).length > 0;

  const handleSave = (data: ScopeOfWork) => {
    save.mutate(data, {
      onSuccess: (updatedData) => reset(updatedData),
    });
  };

  const handleClose = () => {
    dialog.show(ConfirmationDialog, {
      caption: "Confirm Close",
      message: "Are you sure you want to complete and close this scope of work?",
      onAccept: () => {
        loader.show();
        close.mutate(getValues(), {
          onSuccess: (updatedData) => reset(updatedData),
          onSettled: () => loader.hide(),
        });
      },
    });
  };

  const handleCancel = () => {
    dialog.show(ConfirmationDialog, {
      caption: "Cancel SOW",
      message: "You will no longer be able to edit, this cancels the scope of work from the project",
      onAccept: () => {
        loader.show();
        cancel.mutate(getValues(), {
          onSuccess: (updatedData) => reset(updatedData),
          onSettled: () => loader.hide(),
        });
      },
    });
  };

  const handleDelete = () => {
    dialog.show(ConfirmationDialog, {
      caption: "Delete SOW",
      message: "Are you sure you want to delete this scope of work?",
      onAccept: () => {
        loader.show();
        deleteScope.mutate(data.id);
      },
    });
  };

  const handlePrint = () => {
    if (!isDirty) {
      print.showDialog(data);
    } else {
      dialog.show(ConfirmationDialog, {
        caption: "Unsaved Changes",
        message: "You have unsaved changes, would you like to save them now?",
        onAccept: () => {
          save.mutate(data, {
            onSuccess: (updatedData) => {
              reset(updatedData);
              print.showDialog(updatedData);
            },
          });
        },
      });
    }
  };

  return (
    <div className="d-flex gap-2 my-auto">
      <IconButton
        icon={faSave}
        title="Save"
        disabled={!isDirty}
        hidden={role === AccessLevel.GeneralUser}
        onClick={handleSubmit(handleSave)}
        isLoading={save.isLoading}
      />
      <IconButton
        icon={faCheck}
        title="Complete/Close"
        disabled={data.locked || data.status === ScopeOfWorkStatus.New}
        hidden={role === AccessLevel.GeneralUser}
        onClick={handleSubmit(handleClose)}
        isLoading={close.isLoading}
      />
      <IconButton
        icon={faBan}
        title="Cancel"
        disabled={data.locked || data.status === ScopeOfWorkStatus.New}
        hidden={role === AccessLevel.GeneralUser}
        onClick={handleSubmit(handleCancel)}
      />
      <IconButton
        icon={faTrashAlt}
        title="Delete"
        hidden={role === AccessLevel.GeneralUser || role === AccessLevel.WeldingInspector}
        disabled={data.locked}
        onClick={handleDelete}
        isLoading={deleteScope.isLoading}
      />
      <IconButton
        icon={faFilePdf}
        title="Download PDF"
        disabled={data.status === ScopeOfWorkStatus.New}
        onClick={handleSubmit(handlePrint)}
      />
    </div>
  );
}
